import React from "react";
import { ColorPicker as BaseColorPicker, ColorService } from "react-color-palette";

import { Button } from "~/components/button";
import { Input } from "~/components/input";
import { isValidCssColor } from "~/tools/is-valid-css-color";

import "./_base-color-picker.css";

export interface ColorPickerProps {
	/**
	 * Color as a valid CSS color string
	 */
	color: string;
	/**
	 * Array of preset colors as valid CSS color strings
	 * @default PRESET_COLORS constant
	 */
	presetColors?: Array<string>;
	/**
	 * A function which takes color as a CSS color string as an argument
	 */
	onChange: (color: string) => void;
}

export const ColorPicker: React.FC<ColorPickerProps> = ({ color, presetColors = [], onChange }) => {
	const [inputValue, setInputValue] = React.useState(color);

	return (
		<div className="base-color-picker" tabIndex={0}>
			<BaseColorPicker
				color={ColorService.convert("hex", color)}
				height={120}
				hideAlpha
				hideInput={["rgb", "hsv", "hex"]}
				onChange={(newColor) => {
					setInputValue(newColor.hex);
					if (isValidCssColor(newColor.hex)) {
						onChange(newColor.hex);
					}
				}}
			/>
			{presetColors.length > 0 && (
				<div className="flex flex-row justify-between gap-1">
					{presetColors.map((presetColor) => (
						<Button
							className="size-5 rounded-full"
							icon
							key={presetColor}
							onClick={() => {
								setInputValue(presetColor);
								if (isValidCssColor(presetColor)) {
									onChange(presetColor);
								}
							}}
							size="sm"
							variant="neutral"
						>
							<div className="size-full rounded-full" style={{ backgroundColor: presetColor }} />
						</Button>
					))}
				</div>
			)}
			<Input
				onInput={(e) => {
					setInputValue(e.currentTarget.value);
					if (isValidCssColor(e.currentTarget.value)) {
						onChange(e.currentTarget.value);
					}
				}}
				type="text"
				value={inputValue}
				variant="neutral"
			/>
		</div>
	);
};
